import type { Guide, GuideRecordHint } from '../types';

/**
 * S-21.10-17 — akceptacja przewoźnika i stawki przez tradera (scenariusz e2e 21, ścieżka MIX-03 krok 7). Rekordy:
 * DEMO/2026/10/C01 (dwie otwarte oferty) i DEMO/2026/10/C03 z przewoźnikiem już zaakceptowanym w
 * `scenario-state.mjs` (dane pod slajdy z 22.10.2026) — baner i kafelek pulpitu Trade.
 */
const TRADE_ORDER_WITH_OFFERS: GuideRecordHint = {
  description: 'zlecenie w Trade z otwartą ofertą przewoźnika',
  listPath: '/user-offers',
  listFilter: { forklift: 'hide', dzial: 'TRADE', carrierOffers: true },
};

export const guide: Guide = {
  flagKey: 'carrierAcceptance',
  title: 'Akceptacja przewoźnika i stawki przez tradera',
  whatItDoes:
    'W „Ofertach przewoźników” przy każdej otwartej ofercie jest nowy przycisk „Akceptuj przewoźnika i stawkę” — przewoźnik, stawka i waluta przechodzą z oferty, trader dopisuje warunki, a uzasadnienie podaje, gdy okno ostrzega o stracie, ofercie po terminie ważności albo wstrzymanej współpracy z przewoźnikiem. Po akceptacji pozostałe otwarte oferty zlecenia są odrzucane, oferty wystawione na giełdach schodzą same w ciągu minuty, a nowej publikacji nie da się już zlecić. Stan zlecenia się nie zmienia: „sprzedane” ustawi import, gdy przewoźnik albo numer pojazdu pojawi się w TMS, a do tego czasu przypomina o tym pulpit Trade. Akceptację można cofnąć z powodem, a zwrot zlecenia do FTL zostaje możliwy — dotąd rejestr ofert służył tylko do zapisywania ofert.',
  audience: ['Trade'],
  presentationItems: ['S-21.10-17'],
  steps: [
    {
      route: '/user-offers/:orderId/show',
      record: TRADE_ORDER_WITH_OFFERS,
      target: 'trd-negocjacja-akceptuj-przewoznika',
      mockupTestId: 'trd-negocjacja-akceptuj-przewoznika',
      title: 'Akceptacja przy ofercie',
      description:
        'W sekcji „Oferty przewoźników” przy każdej otwartej ofercie stoi „Akceptuj przewoźnika i stawkę”. Marża i jej pasmo przy ofercie podpowiadają, czy stawka się opłaca — strata akceptacji nie blokuje.',
      action: 'Przy wybranej ofercie kliknij „Akceptuj przewoźnika i stawkę”.',
      recorder: { account: 'trade', demo: { kind: 'order', number: 'DEMO/2026/10/C01' } },
    },
    {
      route: '/user-offers/:orderId/show',
      record: TRADE_ORDER_WITH_OFFERS,
      target: 'accept-warnings',
      title: 'Ostrzeżenia w oknie akceptacji',
      description:
        'Okno pokazuje stawkę, marżę z pasmem i skutki akceptacji. Gdy marża jest w paśmie straty, oferta jest po terminie ważności albo współpraca z przewoźnikiem jest wstrzymana, akceptacja wymaga uzasadnienia.',
      action: 'Przeczytaj ostrzeżenia i opis skutków akceptacji.',
      recorder: {
        account: 'trade',
        demo: { kind: 'order', number: 'DEMO/2026/10/C01' },
        actions: [{ kind: 'click', target: 'trd-negocjacja-akceptuj-przewoznika' }],
      },
    },
    {
      route: '/user-offers/:orderId/show',
      record: TRADE_ORDER_WITH_OFFERS,
      target: 'dlg-ok',
      mockupTestId: 'dlg-ok',
      title: 'Warunki, uzasadnienie i akceptacja',
      description:
        'Warunki (np. termin płatności) są opcjonalne, uzasadnienie przy ostrzeżeniu — co najmniej 10 znaków. Po akceptacji na karcie stoi baner z przewoźnikiem, stawką i przypomnieniem o wpisie w TMS, a „Cofnij akceptację” z powodem przywraca ofertę do otwartych.',
      action: 'Wpisz warunki i uzasadnienie, potem kliknij „Akceptuję przewoźnika, stawkę i warunki”.',
      recorder: {
        account: 'trade',
        demo: { kind: 'order', number: 'DEMO/2026/10/C01' },
        actions: [
          { kind: 'click', target: 'trd-negocjacja-akceptuj-przewoznika' },
          { kind: 'fill', target: 'dlg-uzasadnienie', value: 'Klient strategiczny, kierownik zgodził się na tę stawkę.' },
        ],
      },
    },
    {
      route: '/user-offers/:orderId/show',
      record: {
        description: 'zlecenie w Trade z zaakceptowanym przewoźnikiem — z kafelka pulpitu Trade',
        listPath: '/user-offers',
        listFilter: { forklift: 'hide', carrierAccepted: true },
      },
      target: 'carrier-accepted',
      title: 'Baner po akceptacji',
      description:
        'Nad ofertami stoi baner: zaakceptowany przewoźnik, stawka, warunki, kto i kiedy zatwierdził oraz przypomnienie, że „sprzedane” ustawi import, gdy przewoźnik albo numer pojazdu pojawi się w TMS. Pozostałe oferty są odrzucone, a nowej publikacji na giełdę nie da się zlecić.',
      action: 'Wpisz przewoźnika w TMS; gdy akceptacja była pomyłką — kliknij „Cofnij akceptację” i podaj powód.',
      recorder: { account: 'trade', demo: { kind: 'order', number: 'DEMO/2026/10/C03' } },
    },
    {
      route: '/trade-dashboard',
      target: 'trade-tile-carrierAcceptedAwaitingTms',
      title: 'Przypomnienie na pulpicie Trade',
      description:
        'Kafelek „Przewoźnik zaakceptowany — czeka na TMS” liczy zlecenia z zaakceptowanym przewoźnikiem, których TMS jeszcze nie zna. Kliknięcie otwiera listę zleceń z tym samym filtrem; zlecenie schodzi z kafelka, gdy import z TMS ustawi „sprzedane”.',
      action: 'Otwórz „Pulpit Trade” i kliknij kafelek „Przewoźnik zaakceptowany — czeka na TMS”, potem wpisz przewoźnika w TMS.',
      recorder: { account: 'trade' },
    },
  ],
};
